import { useSessionTyped } from '@dreamstack/auth'
import { useTranslation } from '@dreamstack/i18n'
import { ArrowRight } from '@dreamstack/icons/src/icon-components/ArrowRight'
import { SimpleInput } from '@dreamstack/simple-components/src/lib/SimpleInput'
import { SimpleSelect } from '@dreamstack/simple-components/src/lib/SimpleSelect'
import { trpc } from '@dreamstack/trpc/client'
import { cn } from '@dreamstack/util/src/cn'
import MenuItem from '@mui/material/MenuItem'
import Skeleton from '@mui/material/Skeleton'
import filter from 'lodash/filter'
import join from 'lodash/join'
import type { FunctionComponent } from 'react'
import { useCallback } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { FormCheckbox } from '../forms/FormCheckbox'
import {
  AnimateIconTranslationRight,
  useAnimateIconTranslationRight,
} from '../layout/AnimateIconTranslation'
import { useSuccessDialog } from '../confirmation/useSuccessDialog'
import { SimpleMuiButton } from '../layout/SimpleMuiButton'
import { BodyText, HeadingH3 } from '../layout/Texts'
import { usePropAndProjectContactFormSchema } from './usePropAndProjectContactFormSchema'
import { useSubmitContactForm } from './useSubmitContactForm'

const FormFields: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...rest
}) => (
  <div
    className={cn('grid grid-cols-1 gap-px-24 md:grid-cols-2', className)}
    {...rest}
  />
)

const StyledFormContainer: React.FC<
  React.PropsWithChildren<{ isOnDarkBg: boolean; className?: string }>
> = ({ isOnDarkBg, className, ...rest }) => (
  <div
    className={cn(
      'p-px-20 pb-px-48 mt-px-32 md:px-px-40 ml:px-px-48',
      isOnDarkBg
        ? 'bg-accentroDarkGray-full md:mt-0'
        : 'bg-accentroAquaVeryLight-full -mx-px-20 md:-mx-px-40 ml:-mx-px-48',
      className
    )}
    {...rest}
  />
)

//TODO: extract PrefilledValues and NameInput component into its own file ?

const PrefilledValues: FunctionComponent<
  React.PropsWithChildren<{
    loading: boolean
    name: string
    email?: string
  }>
> = ({ loading, name, email }) => {
  const t = useTranslation()
  return (
    <div className="flex space-x-px-16 md:space-x-px-32">
      <div className="min-w-[50px]">
        {/* if fetching contact data -> show loading skeleton*/}
        {loading ? (
          <BodyText className="flex flex-col font-semibold">
            <Skeleton animation={'wave'} className="w-3/4" />
            <Skeleton animation={'wave'} className="w-3/4" />
          </BodyText>
        ) : (
          <BodyText className="flex flex-col font-semibold">
            {/* In some cases, the user has no name property saved in propstack, only show name if it exists in propstack*/}
            {name && <span>Name</span>}
            <span>{`${t('accentro:contactform.email')}`}</span>
          </BodyText>
        )}
      </div>
      <div className="min-w-[50px]">
        {/* if fetching contact data -> show loading skeleton*/}
        {loading ? (
          <BodyText className="flex flex-col">
            <Skeleton animation={'wave'} className="w-full" />
            <Skeleton animation={'wave'} className="w-full" />
          </BodyText>
        ) : (
          <BodyText className="flex flex-col">
            {name && <span>{name}</span>}
            <span>{email ?? ''}</span>
          </BodyText>
        )}
      </div>
    </div>
  )
}

const NameInput: FunctionComponent<
  React.PropsWithChildren<{ control: any; isOnDarkBg: boolean }>
> = ({ control, isOnDarkBg }) => {
  const t = useTranslation()
  return (
    <>
      <div className="col-start-1">
        <Controller
          defaultValue=""
          name="clientSalutation"
          control={control}
          render={({ field: { onChange, value } }) => (
            <SimpleSelect
              label={t('accentro:contactform.salutation')}
              formControlProps={{
                variant: 'filled',
                fullWidth: true,
              }}
              isOnDarkBg={isOnDarkBg}
              selectProps={{
                disableUnderline: true,
                onChange,
                value,
              }}
            >
              <MenuItem value={t('accentro:contactform.mr')}>
                {t('accentro:contactform.mr')}
              </MenuItem>
              <MenuItem value={t('accentro:contactform.ms')}>
                {t('accentro:contactform.ms')}
              </MenuItem>
            </SimpleSelect>
          )}
        />
      </div>
      <div className="col-start-1">
        <Controller
          defaultValue=""
          name="clientFirstName"
          control={control}
          render={({ field }) => (
            <SimpleInput
              {...field}
              label={`${t('accentro:contactform.firstname')}*`}
              fullWidth
              isOnDarkBg={isOnDarkBg}
            />
          )}
        />
      </div>
      <Controller
        defaultValue=""
        name="clientLastName"
        control={control}
        render={({ field }) => (
          <SimpleInput
            {...field}
            label={`${t('accentro:contactform.lastname')}*`}
            fullWidth
            isOnDarkBg={isOnDarkBg}
          />
        )}
      />
    </>
  )
}

export const PropAndProjectContactForm: FunctionComponent<
  React.PropsWithChildren<{
    propId?: number
    projectId?: number
    isOnDarkBg?: boolean
  }>
> = ({ propId, projectId, isOnDarkBg = false }) => {
  const t = useTranslation()
  const yupSchemaResolver = usePropAndProjectContactFormSchema()
  const methods = useForm({ resolver: yupSchemaResolver })
  const { handleSubmit, control, reset, watch } = methods
  const path = t('accentro:contactform.pathReg')
  const { controls } = useAnimateIconTranslationRight()
  const requestPhone = watch('requestPhone')

  const session = useSessionTyped()
  const loggedIn = !!session

  const { data: contactData, isPending: contactDataLoading } =
    trpc.contact.getContact.useQuery(undefined, {
      enabled: loggedIn,
      trpc: { ssr: false },
      staleTime: 0,
    })

  const { SuccessDialog, show } = useSuccessDialog({
    title: t('accentro:contactform.modalTitle'),
    text: t('accentro:contactform.successShort'),
    ctaLabel: t('accentro:contactform.modalCta'),
  })
  const onSuccess = () => {
    show()
    reset()
  }

  const { submitFn, loading } = useSubmitContactForm({
    successCallback: onSuccess,
    formId: 'propertyAndProjectContact',
    type: 'contactform',
    propertyId: propId,
    projectId: projectId,
  })

  const onSubmit = useCallback(
    async (data: any) => {
      let fullData = data
      if (loggedIn) {
        if (contactData?.first_name) {
          fullData = {
            ...data,
            //TODO: possibly change contact api in future and get user data from session object instead of propstack
            clientEmail: contactData?.email,
            clientFirstName: contactData?.first_name,
            clientLastName: contactData?.last_name,
          }
        } else {
          fullData = {
            ...data,
            clientEmail: contactData?.email,
          }
        }
      }
      submitFn(fullData)
    },
    [contactData, loggedIn, submitFn]
  )

  return (
    <StyledFormContainer isOnDarkBg={isOnDarkBg}>
      {SuccessDialog}
      <div className="my-px-24 md:my-px-32 flex flex-col flex-wrap">
        <BodyText>{t('accentro:contactform.infoText')}</BodyText>
      </div>
      <HeadingH3 className="mb-px-20 md:mb-px-24">
        {loggedIn
          ? t('accentro:contactform.inquiry')
          : t('accentro:contactform.contactform')}
      </HeadingH3>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormFields>
            {loggedIn ? (
              // User loggedIn -> prefill his available name and email user data- if no name saved, only show email
              (<>
                <PrefilledValues
                  name={join(
                    filter([contactData?.last_name, contactData?.first_name]),
                    ', '
                  )}
                  email={contactData?.email}
                  loading={contactDataLoading}
                />
                {!contactDataLoading &&
                  // if no name exists in propstack, show name input field
                  !contactData?.first_name && (
                    <NameInput control={control} isOnDarkBg={isOnDarkBg} />
                  )}
              </>)
            ) : (
              // User not loggedIn -> Show name and email input forms to fill out user data
              (<>
                <NameInput control={control} isOnDarkBg={isOnDarkBg} />
                <div className="col-span-full">
                  <Controller
                    defaultValue=""
                    name="clientEmail"
                    control={control}
                    render={({ field }) => (
                      <SimpleInput
                        {...field}
                        label={`${t('accentro:contactform.email')}*`}
                        fullWidth
                        isOnDarkBg={isOnDarkBg}
                      />
                    )}
                  />
                </div>
              </>)
            )}
            <div className="col-span-full">
              <Controller
                defaultValue=""
                name="text"
                control={control}
                render={({ field }) => (
                  <SimpleInput
                    {...field}
                    label={`${t('accentro:contactform.message')}*`}
                    multiline
                    minRows={4}
                    fullWidth
                    isOnDarkBg={isOnDarkBg}
                  />
                )}
              />
            </div>
            <div className="col-span-full">
              <FormCheckbox
                isOnDarkBg={isOnDarkBg}
                propertyName="requestPhone"
                labelComponent={`${t('accentro:contactform.requestPhone')}`}
              />
            </div>
            {requestPhone && (
              <div className="col-span-full">
                <Controller
                  defaultValue=""
                  name="clientPhone"
                  control={control}
                  render={({ field }) => (
                    <SimpleInput
                      {...field}
                      label={`${t('accentro:contactform.phone')}*`}
                      fullWidth
                      isOnDarkBg={isOnDarkBg}
                    />
                  )}
                />
              </div>
            )}
            <div className="col-span-full">
              <FormCheckbox
                isOnDarkBg={isOnDarkBg}
                propertyName="consent"
                labelComponent={`${t('accentro:contactform.consentText')}*`}
              />
            </div>
            <div className="col-span-full pb-px-24">
              <FormCheckbox
                isOnDarkBg={isOnDarkBg}
                propertyName="subscribeNewsletter"
                labelComponent={`${t(
                  'accentro:contactform.consentNewsletterText'
                )}`}
              />
            </div>
            <SimpleMuiButton
              variant="contained"
              type="submit"
              color="primary"
              disabled={loading}
              className="min-w-min whitespace-nowrap"
              endIcon={
                <AnimateIconTranslationRight animate={controls}>
                  <ArrowRight />
                </AnimateIconTranslationRight>
              }
            >
              {t('accentro:contactform.send')}
            </SimpleMuiButton>
          </FormFields>
        </form>
      </FormProvider>
    </StyledFormContainer>
  );
}
